import { removeAuthHeaders, setAuthHeaders } from '@/plugins/axios'
import * as Sentry from '@sentry/vue'
import { useQueryClient } from '@tanstack/vue-query'
import dayjs from 'dayjs'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { LocalStorageKey } from '@/stores/stores.type'

interface JWT {
  iat: number
  exp: number
  aud: string
  iss: string
  sub: string
}

const isJWTValid = (jwt: string) => {
  const payloadBase64 = jwt.split('.')[1]
  const payload: JWT = JSON.parse(atob(payloadBase64))
  const expiration = dayjs(payload.exp * 1000)
  const expired = expiration.diff(dayjs().subtract(5, 'minutes'), 'seconds') < 0
  return !expired
}

export const useAuthStore = defineStore('auth', () => {
  const queryClient = useQueryClient()
  const storedToken = localStorage.getItem(LocalStorageKey.AccessToken)
  const storedTenant = localStorage.getItem(LocalStorageKey.TenantKey)
  const accessToken = ref<string | null>(null)
  const tenantKey = ref<string | null>(null)
  const firstLogin = ref<{ username: string; password: string; tenant: string }>()

  const setCurrentAuth = (tenant: string, token: string) => {
    accessToken.value = token
    tenantKey.value = tenant
    localStorage.setItem(LocalStorageKey.TenantKey, tenant)
    localStorage.setItem(LocalStorageKey.AccessToken, token)
    Sentry.setTag('tenant', tenant)
    setAuthHeaders(tenant, token)
    queryClient.invalidateQueries({
      queryKey: [],
    })
  }

  const logout = () => {
    accessToken.value = null
    localStorage.removeItem(LocalStorageKey.AccessToken)
    removeAuthHeaders()
    Sentry.setUser(null)
    Sentry.setTag('tenant', null)
  }

  const logged = () => accessToken.value != null

  if (storedTenant && storedToken && isJWTValid(storedToken)) {
    setCurrentAuth(storedTenant, storedToken)
  } else if (storedTenant) {
    tenantKey.value = storedTenant
  }

  return { accessToken, tenantKey, firstLogin, setCurrentAuth, logged, logout }
})
